import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDateShort } from '@/lib/dates';
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronRight, Loader2, Plus, Search, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  createSignupRef,
  fetchSignupRefs,
  normalizeRefCode,
  type SignupRefRow,
} from '@/api/signup-refs';

export const Route = createFileRoute('/admin/signup-codes')({
  component: SignupCodesPage,
});

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return formatDateShort(iso);
}

function SignupCodesPage() {
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const codesQuery = useQuery({
    queryKey: ['signup-refs'],
    queryFn: fetchSignupRefs,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
  });

  const all = codesQuery.data ?? [];
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    // Single combined search across code + name + purpose.
    return all.filter(
      (r) =>
        r.code.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        (r.purpose ?? '').toLowerCase().includes(q),
    );
  }, [all, search]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold">Signup Codes</h1>
          <p className="text-sm text-muted-foreground">
            Attribution codes captured at signup (QR / marketing links). Click a code to see who
            signed up with it.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} className="flex-shrink-0">
          <Plus className="h-4 w-4 mr-1" /> New code
        </Button>
      </div>

      <CreateCodeDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        existingCodes={all.map((r) => r.code)}
        onCreated={() => codesQuery.refetch()}
      />

      {/* Search */}
      <div className="px-6 py-3 border-b border-border">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code, name, or purpose…"
            className="w-full pl-9 pr-9 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {codesQuery.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : codesQuery.error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {codesQuery.error instanceof Error ? codesQuery.error.message : 'Failed to load codes'}
            </AlertDescription>
          </Alert>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            {all.length === 0 ? 'No signup codes yet.' : 'No codes match your search.'}
          </p>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border">
            {rows.map((r: SignupRefRow) => (
              <Link
                key={r.id}
                to="/admin/signup-codes/$code"
                params={{ code: r.code }}
                className="flex items-center gap-4 px-4 py-3 hover:bg-secondary/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium truncate">{r.code}</span>
                    <Badge variant="secondary">{r.count}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{r.name}</p>
                  {r.purpose && (
                    <p className="text-xs text-muted-foreground/80 truncate">{r.purpose}</p>
                  )}
                </div>
                <div className="hidden sm:block text-right text-xs text-muted-foreground flex-shrink-0">
                  <div>{formatDate(r.date_started)} → {formatDate(r.date_finished)}</div>
                  <div>created {formatDate(r.created_at)}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CreateCodeDialog({
  open,
  onOpenChange,
  existingCodes,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existingCodes: string[];
  onCreated: () => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [dateStarted, setDateStarted] = useState('');
  const [dateFinished, setDateFinished] = useState('');

  const normalized = normalizeRefCode(code);
  const duplicate = normalized.length > 0 && existingCodes.includes(normalized);

  const mutation = useMutation({
    mutationFn: () =>
      createSignupRef({
        code,
        name,
        purpose,
        date_started: dateStarted || null,
        date_finished: dateFinished || null,
      }),
    onSuccess: () => {
      onCreated();
      setCode('');
      setName('');
      setPurpose('');
      setDateStarted('');
      setDateFinished('');
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New signup code</DialogTitle>
          <DialogDescription>
            Codes are normalised to lower-case (letters, numbers, <code>. _ -</code>) so they match
            what a signup produces.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="sc-code">Code</Label>
            <Input
              id="sc-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="expo-2026"
            />
            {normalized && normalized !== code && (
              <p className="text-xs text-muted-foreground">
                Stored as <span className="font-mono">{normalized}</span>
              </p>
            )}
            {duplicate && <p className="text-xs text-destructive">This code already exists.</p>}
          </div>

          <div className="space-y-1">
            <Label htmlFor="sc-name">Name</Label>
            <Input
              id="sc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Expo 2026 booth"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="sc-purpose">Purpose</Label>
            <Input
              id="sc-purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Trade-show QR handout"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="sc-start">Start date</Label>
              <Input
                id="sc-start"
                type="date"
                value={dateStarted}
                onChange={(e) => setDateStarted(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="sc-end">End date</Label>
              <Input
                id="sc-end"
                type="date"
                value={dateFinished}
                onChange={(e) => setDateFinished(e.target.value)}
              />
            </div>
          </div>

          {mutation.error && (
            <p className="text-xs text-destructive">
              {mutation.error instanceof Error ? mutation.error.message : 'Failed to create code'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!normalized || duplicate || mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
